import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add as AddIcon, CardMembership as MembershipIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton, { ManagementContentSkeleton } from '../../components/layout/ManagementPageSkeleton';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EnrollCustomerMembershipDialog from './EnrollCustomerMembershipDialog';
import MembershipPeriodPaymentDialog from './MembershipPeriodPaymentDialog';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import { getLocalizedValue } from '../../constants/Languages';
import { allowsInStore, normalizeCustomerPaymentMode } from '../../constants/CustomerPaymentMode';
import MembershipPlanService from '../../services/membershipPlanService';
import StoreService from '../../services/storeService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import {
  createInStoreMembership,
  getManagementMemberships,
  markMembershipPeriodPaid,
  cancelStoreCustomerMembership,
  resumeStoreCustomerMembershipCancellation,
} from '../../services/storeCustomerMembershipService';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { resolveMembershipPaidThroughUtc } from '../../utils/membershipBookingUtils';
import { BRAND } from '../../theme/brandTokens';

const getMembershipStatusKey = (status) => {
  if (!status) {
    return 'unknown';
  }

  return `${status.charAt(0).toLowerCase()}${status.slice(1)}`;
};

const getPaymentChannelKey = (channel) => {
  if (!channel) {
    return 'unknown';
  }

  return `${channel.charAt(0).toLowerCase()}${channel.slice(1)}`;
};

const MembershipMobileCard = ({
  membership,
  planName,
  statusLabel,
  channelLabel,
  periodLabel,
  priceLabel,
  canMarkPaid,
  markingPaid,
  isAdvancePeriodPayment,
  canCancel,
  canResumeCancellation,
  canceling,
  resumingCancellation,
  cancelsOnLabel,
  onMarkPaid,
  onCancel,
  onResumeCancellation,
  t,
}) => (
  <Box
    sx={{
      border: `1px solid ${BRAND.line}`,
      borderRadius: 2,
      p: 2,
      bgcolor: BRAND.surface,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1.25 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: BRAND.text,
            lineHeight: 1.3,
            wordBreak: 'break-word',
          }}
        >
          {membership.customerName || t('customerMembershipsManagement.table.unnamedCustomer')}
        </Typography>
        {membership.customerEmail ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.25, wordBreak: 'break-word' }}
          >
            {membership.customerEmail}
          </Typography>
        ) : null}
      </Box>
      <Chip
        size="small"
        label={statusLabel}
        sx={{ flexShrink: 0, fontWeight: 600 }}
      />
    </Box>
    {cancelsOnLabel ? (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {cancelsOnLabel}
      </Typography>
    ) : null}

    <Stack spacing={1}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>
          {t('customerMembershipsManagement.table.plan')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' }}>
          {planName}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>
          {t('customerMembershipsManagement.table.channel')}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right' }}>
          {channelLabel}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>
          {t('customerMembershipsManagement.table.period')}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'right', wordBreak: 'break-word' }}>
          {periodLabel}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3, flexShrink: 0 }}>
          {t('customerMembershipsManagement.table.price')}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: BRAND.accentText, textAlign: 'right' }}>
          {priceLabel}
        </Typography>
      </Box>
    </Stack>

    {(canMarkPaid || canCancel || canResumeCancellation) ? (
      <Stack spacing={1} sx={{ mt: 1.5, pt: 1.25, borderTop: `1px solid ${BRAND.lineSoft}` }}>
        {canMarkPaid ? (
          <PrimaryActionButton
            fullWidth
            size="small"
            disabled={markingPaid || canceling || resumingCancellation}
            onClick={onMarkPaid}
            startIcon={markingPaid ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {markingPaid
              ? t('customerMembershipsManagement.markPaid.marking')
              : t(isAdvancePeriodPayment
                ? 'customerMembershipsManagement.markPaid.payNextPeriod'
                : 'customerMembershipsManagement.markPaid.action')}
          </PrimaryActionButton>
        ) : null}
        {canResumeCancellation ? (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            disabled={canceling || resumingCancellation}
            onClick={onResumeCancellation}
          >
            {resumingCancellation
              ? t('customerMembershipsManagement.cancel.resuming')
              : t('customerMembershipsManagement.cancel.resume')}
          </Button>
        ) : null}
        {canCancel ? (
          <Button
            fullWidth
            size="small"
            variant="outlined"
            color="error"
            disabled={markingPaid || canceling || resumingCancellation}
            onClick={onCancel}
          >
            {canceling
              ? t('customerMembershipsManagement.cancel.canceling')
              : t('customerMembershipsManagement.cancel.action')}
          </Button>
        ) : null}
      </Stack>
    ) : null}
  </Box>
);

const CustomerMembershipsManagementPanel = ({
  embedded = false,
  planContext = null,
  planContextLoading = false,
  memberships: managedMemberships,
  onMembershipsChange,
  membershipsLoading = false,
  membershipsError: managedMembershipsError,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { storeId } = useParams();
  const { showError, showSuccess } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const isManaged = managedMemberships !== undefined;

  const [internalMemberships, setInternalMemberships] = useState([]);
  const [standalonePlanContext, setStandalonePlanContext] = useState(null);
  const [standaloneStore, setStandaloneStore] = useState(null);
  const [loading, setLoading] = useState(!isManaged);
  const [error, setError] = useState(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [periodPaymentMembership, setPeriodPaymentMembership] = useState(null);
  const [cancelMembership, setCancelMembership] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);
  const [resumingCancellationId, setResumingCancellationId] = useState(null);

  const memberships = isManaged ? managedMemberships : internalMemberships;
  const setMemberships = isManaged ? onMembershipsChange : setInternalMemberships;
  const displayError = isManaged ? managedMembershipsError : error;

  const resolvedPlanContext = planContext ?? standalonePlanContext;
  const currency = resolvedPlanContext?.currency || standaloneStore?.currency || 'EUR';
  const membershipPaymentMode = normalizeCustomerPaymentMode(
    resolvedPlanContext?.membershipCustomerPaymentMode ?? standaloneStore?.membershipCustomerPaymentMode,
  );

  const canManagePayments = hasPermission(Permissions.MembershipsPaymentUpdate);
  const canEnrollInStore = allowsInStore(membershipPaymentMode);

  const activePlans = useMemo(
    () => (resolvedPlanContext?.plans || []).filter((plan) => MembershipPlanService.isMembershipPlanActive(plan.status)),
    [resolvedPlanContext?.plans],
  );

  const loadMemberships = useCallback(async () => {
    if (!storeId) {
      setError(t('customerMembershipsManagement.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const requestKey = `customer-memberships-management-${storeId}`;
      const membershipsResult = await requestDeduplicator.deduplicate(
        requestKey,
        () => getManagementMemberships(storeId),
      );

      setMemberships(membershipsResult);
    } catch (loadError) {
      console.error('Failed to load customer memberships:', loadError);
      setError(loadError?.response?.data?.message || t('customerMembershipsManagement.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [setMemberships, storeId, t]);

  const loadStandaloneContext = useCallback(async () => {
    if (!storeId || planContext) {
      return;
    }

    const requestKey = `getMembershipPlanManagement-${storeId}`;

    try {
      setLoading(true);
      setError(null);

      const [membershipsResult, planData, storeData] = await requestDeduplicator.deduplicate(
        `${requestKey}-standalone`,
        async () => Promise.all([
          getManagementMemberships(storeId),
          MembershipPlanService.getMembershipPlanManagementData(storeId),
          StoreService.getStore(storeId),
        ]),
      );

      setMemberships(membershipsResult);
      setStandalonePlanContext(planData);
      setStandaloneStore(storeData);
    } catch (loadError) {
      console.error('Failed to load customer memberships:', loadError);
      setError(loadError?.response?.data?.message || t('customerMembershipsManagement.error.loading'));
    } finally {
      setLoading(false);
    }
  }, [planContext, setMemberships, storeId, t]);

  useEffect(() => {
    if (isManaged || !storeId) {
      return;
    }

    if (planContext) {
      loadMemberships();
      return;
    }

    loadStandaloneContext();
  }, [isManaged, loadMemberships, loadStandaloneContext, planContext, storeId]);

  const handleOpenEnroll = () => {
    setEnrollOpen(true);
  };

  const handleCloseEnroll = () => {
    if (enrolling) {
      return;
    }

    setEnrollOpen(false);
  };

  const handleEnroll = async ({ customerId, planId, paymentMethod, payments }) => {
    try {
      setEnrolling(true);
      const createdMembership = await createInStoreMembership(storeId, {
        customerId,
        planId,
        paymentMethod,
        payments,
      });
      if (setMemberships) {
        setMemberships((current) => [createdMembership, ...current]);
      }
      setEnrollOpen(false);
      showSuccess(t('customerMembershipsManagement.enroll.success'));
    } catch (enrollError) {
      console.error('Failed to enroll customer:', enrollError);
      showError(enrollError?.response?.data?.message || t('customerMembershipsManagement.enroll.error'));
    } finally {
      setEnrolling(false);
    }
  };

  const handleMarkPeriodPaid = async ({ paymentMethod, payments }) => {
    if (!periodPaymentMembership?.id) {
      return;
    }

    try {
      setMarkingPaidId(periodPaymentMembership.id);
      const updatedMembership = await markMembershipPeriodPaid(
        storeId,
        periodPaymentMembership.id,
        { paymentMethod, payments },
      );
      if (setMemberships && updatedMembership) {
        setMemberships((current) => current.map((membership) => (
          membership.id === periodPaymentMembership.id ? updatedMembership : membership
        )));
      }
      setPeriodPaymentMembership(null);
      showSuccess(t(
        periodPaymentMembership.isAdvancePeriodPayment
          ? 'customerMembershipsManagement.markPaid.payNextSuccess'
          : 'customerMembershipsManagement.markPaid.success',
      ));
    } catch (markPaidError) {
      console.error('Failed to mark membership period paid:', markPaidError);
      showError(markPaidError?.response?.data?.message || t('customerMembershipsManagement.markPaid.error'));
    } finally {
      setMarkingPaidId(null);
    }
  };

  const applyMembershipUpdate = (membershipId, updatedMembership) => {
    if (!setMemberships) {
      return;
    }

    if (updatedMembership) {
      setMemberships((current) => current.map((membership) => (
        membership.id === membershipId ? updatedMembership : membership
      )));
      return;
    }

    setMemberships((current) => current.filter((membership) => membership.id !== membershipId));
  };

  const handleCancelMembership = async () => {
    if (!cancelMembership?.id) {
      return;
    }

    const membershipId = cancelMembership.id;

    try {
      setCancelingId(membershipId);
      const updatedMembership = await cancelStoreCustomerMembership(storeId, membershipId);
      applyMembershipUpdate(membershipId, updatedMembership);
      setCancelMembership(null);
      showSuccess(t('customerMembershipsManagement.cancel.success', {
        date: formatUtcDate(
          updatedMembership?.cancelsAtUtc
          || resolveMembershipPaidThroughUtc(cancelMembership)
          || cancelMembership.currentPeriodEndUtc,
        ),
      }));
    } catch (cancelError) {
      console.error('Failed to cancel membership:', cancelError);
      showError(cancelError?.response?.data?.message || t('customerMembershipsManagement.cancel.error'));
    } finally {
      setCancelingId(null);
    }
  };

  const handleResumeCancellation = async (membership) => {
    if (!membership?.id) {
      return;
    }

    try {
      setResumingCancellationId(membership.id);
      const updatedMembership = await resumeStoreCustomerMembershipCancellation(storeId, membership.id);
      applyMembershipUpdate(membership.id, updatedMembership);
      showSuccess(t('customerMembershipsManagement.cancel.resumeSuccess'));
    } catch (resumeError) {
      console.error('Failed to resume membership cancellation:', resumeError);
      showError(resumeError?.response?.data?.message || t('customerMembershipsManagement.cancel.resumeError'));
    } finally {
      setResumingCancellationId(null);
    }
  };

  if ((embedded && (planContextLoading || !planContext)) || (isManaged && membershipsLoading) || (!isManaged && loading)) {
    return embedded
      ? <ManagementContentSkeleton variant="detail" />
      : <ManagementPageSkeleton />;
  }

  const getMembershipDisplay = (membership) => {
    const planName = getLocalizedValue(
      membership.planName,
      i18n.language,
      undefined,
      t('customer.memberships.card.unnamed'),
    );
    const statusKey = getMembershipStatusKey(membership.status);
    const channelKey = getPaymentChannelKey(membership.paymentChannel);
    const periodLabel = membership.currentPeriodStartUtc && membership.currentPeriodEndUtc
      ? t('customerMembershipsManagement.table.periodRange', {
          start: formatUtcDate(membership.currentPeriodStartUtc),
          end: formatUtcDate(membership.currentPeriodEndUtc),
        })
      : t('customerMembershipsManagement.table.noPeriod');

    return {
      planName,
      statusLabel: t(`customer.memberships.status.${statusKey}`, { defaultValue: membership.status }),
      channelLabel: t(`customerMembershipsManagement.paymentChannel.${channelKey}`, {
        defaultValue: membership.paymentChannel,
      }),
      periodLabel,
      priceLabel: formatCurrency(membership.price, membership.currency || currency),
      cancelsOnLabel: membership.cancelAtPeriodEnd && (membership.cancelsAtUtc || resolveMembershipPaidThroughUtc(membership))
        ? t('customerMembershipsManagement.cancel.cancelsOn', {
            date: formatUtcDate(membership.cancelsAtUtc || resolveMembershipPaidThroughUtc(membership)),
          })
        : null,
    };
  };

  const panelBody = (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {canManagePayments && canEnrollInStore ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            mb: { xs: 2.5, sm: 4 },
            pt: { xs: 0.5, sm: 1 },
          }}
        >
          <PrimaryActionButton
            startIcon={<AddIcon />}
            onClick={handleOpenEnroll}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              px: 4,
              py: 1.5,
            }}
          >
            {t('customerMembershipsManagement.enroll.action')}
          </PrimaryActionButton>
        </Box>
      ) : null}

      {memberships.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
          <MembershipIcon sx={{ fontSize: 48, color: BRAND.accentText, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('customerMembershipsManagement.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('customerMembershipsManagement.empty.subtitle')}
          </Typography>
        </Box>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {memberships.map((membership) => {
            const display = getMembershipDisplay(membership);
            return (
              <MembershipMobileCard
                key={membership.id}
                membership={membership}
                planName={display.planName}
                statusLabel={display.statusLabel}
                channelLabel={display.channelLabel}
                periodLabel={display.periodLabel}
                priceLabel={display.priceLabel}
                canMarkPaid={canManagePayments && membership.canMarkPeriodPaid}
                markingPaid={markingPaidId === membership.id}
                isAdvancePeriodPayment={Boolean(membership.isAdvancePeriodPayment)}
                canCancel={canManagePayments && membership.canCancel}
                canResumeCancellation={canManagePayments && membership.canResumeCancellation}
                canceling={cancelingId === membership.id}
                resumingCancellation={resumingCancellationId === membership.id}
                cancelsOnLabel={display.cancelsOnLabel}
                onMarkPaid={() => setPeriodPaymentMembership(membership)}
                onCancel={() => setCancelMembership(membership)}
                onResumeCancellation={() => handleResumeCancellation(membership)}
                t={t}
              />
            );
          })}
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('customerMembershipsManagement.table.customer')}</TableCell>
                <TableCell>{t('customerMembershipsManagement.table.plan')}</TableCell>
                <TableCell>{t('customerMembershipsManagement.table.status')}</TableCell>
                <TableCell>{t('customerMembershipsManagement.table.channel')}</TableCell>
                <TableCell>{t('customerMembershipsManagement.table.period')}</TableCell>
                <TableCell align="right">{t('customerMembershipsManagement.table.price')}</TableCell>
                <TableCell align="right">{t('customerMembershipsManagement.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberships.map((membership) => {
                const display = getMembershipDisplay(membership);

                return (
                  <TableRow key={membership.id} hover>
                    <TableCell>
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {membership.customerName || t('customerMembershipsManagement.table.unnamedCustomer')}
                        </Typography>
                        {membership.customerEmail ? (
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {membership.customerEmail}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{display.planName}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip size="small" label={display.statusLabel} />
                        {display.cancelsOnLabel ? (
                          <Typography variant="caption" color="text.secondary">
                            {display.cancelsOnLabel}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>{display.channelLabel}</TableCell>
                    <TableCell>{display.periodLabel}</TableCell>
                    <TableCell align="right">{display.priceLabel}</TableCell>
                    <TableCell align="right">
                      <Stack spacing={1} alignItems="flex-end">
                        {canManagePayments && membership.canMarkPeriodPaid ? (
                          <PrimaryActionButton
                            size="small"
                            disabled={markingPaidId === membership.id || cancelingId === membership.id}
                            onClick={() => setPeriodPaymentMembership(membership)}
                            startIcon={markingPaidId === membership.id
                              ? <CircularProgress size={14} color="inherit" />
                              : null}
                          >
                            {markingPaidId === membership.id
                              ? t('customerMembershipsManagement.markPaid.marking')
                              : t(membership.isAdvancePeriodPayment
                                ? 'customerMembershipsManagement.markPaid.payNextPeriod'
                                : 'customerMembershipsManagement.markPaid.action')}
                          </PrimaryActionButton>
                        ) : null}
                        {canManagePayments && membership.canResumeCancellation ? (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={resumingCancellationId === membership.id}
                            onClick={() => handleResumeCancellation(membership)}
                          >
                            {resumingCancellationId === membership.id
                              ? t('customerMembershipsManagement.cancel.resuming')
                              : t('customerMembershipsManagement.cancel.resume')}
                          </Button>
                        ) : null}
                        {canManagePayments && membership.canCancel ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={cancelingId === membership.id || markingPaidId === membership.id}
                            onClick={() => setCancelMembership(membership)}
                          >
                            {cancelingId === membership.id
                              ? t('customerMembershipsManagement.cancel.canceling')
                              : t('customerMembershipsManagement.cancel.action')}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <>
      {displayError ? (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            mx: embedded ? { xs: 2, sm: 4 } : 0,
            mt: embedded ? { xs: 2, sm: 4 } : 0,
          }}
        >
          {displayError}
        </Alert>
      ) : null}

      {embedded ? panelBody : <ManagementPageCard>{panelBody}</ManagementPageCard>}

      <EnrollCustomerMembershipDialog
        open={enrollOpen}
        onClose={handleCloseEnroll}
        onEnroll={handleEnroll}
        enrolling={enrolling}
        storeId={storeId}
        activePlans={activePlans}
        currencyCode={currency}
      />

      <MembershipPeriodPaymentDialog
        open={Boolean(periodPaymentMembership)}
        onClose={() => {
          if (markingPaidId) {
            return;
          }

          setPeriodPaymentMembership(null);
        }}
        onConfirm={handleMarkPeriodPaid}
        confirming={Boolean(periodPaymentMembership && markingPaidId === periodPaymentMembership.id)}
        title={t(periodPaymentMembership?.isAdvancePeriodPayment
          ? 'customerMembershipsManagement.markPaid.payNextTitle'
          : 'customerMembershipsManagement.markPaid.title')}
        description={t(periodPaymentMembership?.isAdvancePeriodPayment
          ? 'customerMembershipsManagement.markPaid.payNextDescription'
          : 'customerMembershipsManagement.markPaid.description')}
        confirmText={t(periodPaymentMembership?.isAdvancePeriodPayment
          ? 'customerMembershipsManagement.markPaid.payNextPeriod'
          : 'customerMembershipsManagement.markPaid.action')}
        amount={periodPaymentMembership?.price || 0}
        currencyCode={periodPaymentMembership?.currency || currency}
      />

      <ConfirmDialog
        open={Boolean(cancelMembership)}
        onClose={() => {
          if (cancelingId) {
            return;
          }

          setCancelMembership(null);
        }}
        onConfirm={handleCancelMembership}
        loading={Boolean(cancelMembership && cancelingId === cancelMembership.id)}
        closeOnConfirm={false}
        title={t('customerMembershipsManagement.cancel.confirmTitle')}
        message={t('customerMembershipsManagement.cancel.confirmMessage', {
          date: formatUtcDate(
            cancelMembership?.cancelsAtUtc
            || resolveMembershipPaidThroughUtc(cancelMembership)
            || cancelMembership?.currentPeriodEndUtc,
          ),
        })}
        confirmText={t('customerMembershipsManagement.cancel.action')}
        cancelText={t('common.cancel')}
        confirmColor="error"
      />
    </>
  );
};

export default CustomerMembershipsManagementPanel;
